import {
    EzObject,
    EzString,
    EzPromise,
    EzJson
} from '/ezlibrary/helpers/EzHelpers.js';
import {
    EzRegistrationState,
    EzEnvironment
} from '/ezlibrary/enums/EzEnumerations.js';
import { EzClass } from '/ezlibrary/EzClass.js';
import { EzEventEngine } from '/ezlibrary/ez-event-engine.js';
import { EzHttpHelper } from '/public/javascript/common/ezclocker-http-helper.js';
import { EzNavigation } from '/public/javascript/common/ezclocker-navigation-helper.js';
import { EzPropertyReader } from '/ezlibrary/configuration/EzPropertyReader.js';
import { EzVersionChecker } from '/ezlibrary/EzVersionChecker.js';

/**
 * @class
 * @extends {EzClass}
 * @description
 * Provides the application version to the JS code base
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzVersion } from '/ezlibrary/EzVersion.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Check if ready:
 *     globalThis.ezApi.ezclocker[EzVersion.ezApiName] &&
 *     globalThis.ezApi.ezclocker[EzVersion.ezApiName].ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Hook onReady event:
 *     document.addEventListener(
 *         EzVersion.ezEventNames.onReady,
 *         {class_name}.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Access singleton instance outside this module:
 *     ezApi.ezclocker.ezVersion
 * Access singleton instance within this module:
 *     EzVersion.ezInstance
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzVersion extends EzClass {
    /**
     * @private @field
     * Stores the version information for ezClocker services.
     * @type {object}
     */
    #ezVersionInfo = {
        targetEnvironment: EzEnvironment.PRD,
        version: 'rUNKNOWN',
        build: '0',
        buildDate: EzString.EMPTY,
        branch: EzString.EMPTY
    };
    /**
     * @public @property @getter
     * Gets the version information for ezClocker services.
     * @returns {object}
     */
    get ezVersionInfo() {
        return this.#ezVersionInfo;
    }
    /**
     * @public @property @setter
     * Sets the version information for ezClocker services.
     * @param {undefined|null|object} ezVersionInfo
     */
    set ezVersionInfo(ezVersionInfo) {
        if (!EzObject.isValid(ezVersionInfo)) {
            const nowMoment = globalThis.ezApi.ezclocker.ezDateTime.ezNow();

            this.#ezVersionInfo = {
                targetEnvironment: EzEnvironment.PRD,
                environment: EzEnvironment.PRD,
                version: `r${globalThis.ezApi.ezclocker.ezDateTime.ezToVersionStamp(nowMoment)}`,
                build: `${nowMoment.getMilliseconds()}`,
                buildDate: `${globalThis.ezApi.ezclocker.ezDateTime.ezToIsoDate(nowMoment)}`,
                branch: '????'
            };
        } else {
            const targetEnvironment = ezVersionInfo.enviornmentId.toUpperCase();

            this.#ezVersionInfo.targetEnvironment = EzString.stringOrDefault(
                EzEnvironment.ezAsEnum(targetEnvironment),
                EzEnvironment.PRD);

            this.#ezVersionInfo.version = EzString.stringOrDefault(
                ezVersionInfo.version,
                'rUNKNOWN');

            this.#ezVersionInfo.build = EzString.stringOrDefault(
                ezVersionInfo.build,
                '0');

            this.#ezVersionInfo.buildDate = EzString.stringOrEmpty(ezVersionInfo.buildDate);

            this.#ezVersionInfo.branch = EzString.stringOrEmpty(ezVersionInfo.branch);
        }
    }
    /**
     * @public @method
     * Gets the version information object
     * @returns {string}
     * @deprecated
     * Migrate to using the property: globalThis.ezApi.ezclocker.ezVersion.ezVersionInfo
     */
    ezGetVersionInfo() {
        return EzVersion.ezInstance.ezVersionInfo;
    }

    /**
     * @public @readonly @property
     * Gets the enviornment id
     * @returns {string}
     * A valid enumeration value from EzEnvironment
     */
    get ezEnvironment() {
        return this.ezVersionInfo.environment;
    }
    /**
     * @public @method
     * Gets the current ezClocker environment id
     * @returns {string}
     * A valid enumeration value from EzEnvironment
     * @deprecated
     * Migrate to the property:
     *     globalThis.ezApi.ezclocker.ezVersion.ezEnvironment
     */
    ezGetEnvironment() {
        return EzVersion.ezInstance.ezVersionInfo.environment;
    }

    /**
     * @public @readonly @property
     * Gets the version of the ezClocker website and services
     * @returns {string}
     * A valid enumeration value from EzEnvironment
     */
    get ezVersion() {
        return this.ezVersionInfo.version;
    }
    /**
     * @public @method
     * Gets the version of the ezClocker website and services
     * @returns {string}
     * @deprecated
     * Migrate to the property:
     *     globalThis.ezApi.ezclocker.ezVersion.ezVersion
     */
    ezGetVersion() {
        return EzVersion.ezInstance.ezVersionInfo.version;
    }

    /**
     * @public @readonly @property
     * Gets the build number for the ezClocker website and services
     * @returns {string}
     */
    get ezBuild() {
        return this.ezVersionInfo.build;
    }
    /**
     * @public @method
     * Gets the current ezClocker version's build number.
     * @returns {string}
     * @deprecated
     * Migrate to the property: ezApi.ezclocker.ezVersion.ezBuild
     */
    ezGetBuild() {
        return EzVersion.ezInstance.ezVersionInfo.build;
    }

    /**
     * @public @method
     * Initializes EzVersion
     * @returns {EzVersion}
     */
    ezInit() {
        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzVersion.ezApiName,
            EzVersion.ezEventNames.onInitialized);

        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzVersion.ezApiName,
            EzVersion.ezEventNames.onError);

        EzVersion.ezInstance.ezLoadServiceVersionInfo();

        return EzVersion.ezInstance;
    }

    /**
     * @public @method
     * Obtains the version info from the ezClocker service.
     * @returns {Promise.resolve}
     */
    ezLoadServiceVersionInfo() {
        return EzPromise.asyncAction(
            (finished) => globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
                globalThis.ezApi.ezclocker.ezNavigation.ezGetPublicApiServiceUrl('version', 'v1'))
                .then(
                    (response) => {
                        EzVersion.ezInstance.ezVersionInfo = response;

                        globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                            EzVersion.ezEventNames.onInitialized,
                            globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                                EzVersion.ezApiName,
                                'EzVersion information is ready.',
                                {
                                    response,
                                    ezVersion: EzVersion.ezInstance
                                }));

                        return EzPropertyReader.ezReadProperty('ezclocker.environment', EzVersionChecker.DEFAULT_BUILD_ENVIRONMENT)
                            .then(
                                (environmentValue) => {
                                    EzVersion.ezInstance.ezVersionInfo.environment = EzString.stringWithTextOrDefault(
                                        environmentValue,
                                        'prd');

                                    return finished();
                                });
                    },
                    (eResponse) => {
                        globalThis.ezApi.ezclocker.ezLogger.error(
                            EzString.em`
                                Unable to obtain the ezClocker service version information.
                                Error: ${EzJson.toJson(eResponse)}`);

                        globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                            EzVersion.ezEventNames.onError,
                            globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                                EzVersion.ezApiName,
                                'Error obtaining version information.',
                                {
                                    response: eResponse,
                                    ezVersion: EzVersion.ezInstance
                                }));

                        return finished();
                    }));
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~


    /**
     * @public @static @readonly @property
     * Gets the name of this class's singleton instance when registered with ezApi.
     * @returns {String}
     */
    static get ezApiName() {
        return 'ezVersion';
    }

    /**
     * @public @static @readonly @property
     * Gets an object of event names that this class may trigger.
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzVersion_Ready',
            onInitialized: 'ezOn_EzVersion_Initialized',
            onError: 'ezOn_EzVersion_Error'
        };
    }

    /**
      * @static
      * @private @field
      * Stores the singleton instance of this class that was created by and registerd with EzApi.
      * @type {EzVersion}
      */
    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzVersion.ezApiName]
        ? globalThis.ezApi.ezclocker[EzVersion.ezApiName]
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     * @returns {EzVersion}
     */
    static get ezInstance() {
        return EzVersion.#ezInstance;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of of this class that is registered with EzApi.
     * @param {EzVersion} instance
     */
    static set ezInstance(instance) {
        if (null != EzVersion.#ezInstance) {
            throw new Error('EzVersion\'s singleton instance is already reigstered with EzApi.');
        }

        EzVersion.#ezInstance = instance;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
        Default value is NULL
        Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     * @type {string}
        A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzVersion.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     * @returns {string}
        A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzVersion.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
        A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzVersion.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Gets true when all required dependencies for this class report ready.
        In otherwords, the require dependency's singleton instance is created
        and is registered with and accessable from EzApi with ezApi.ezclocker.{dependency_name}.
     * @returns {boolean}
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzVersion.ezApiRegistrationState && globalThis?.ezApi?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzEventEngine.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzHttpHelper.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzNavigation.ezApiName]?.ready;
    }

    /**
       @static
       @private @readonly @property
       Returns if this class's singleton instance is registered with ezApi yet.
       @returns {boolean}
    */
    static get #ezIsRegistered() {
        return null != EzVersion.ezInstance &&
            EzRegistrationState.REGISTERED === EzVersion.ezApiRegistrationState;
    }

    /**
     * @static
     * @private @method
        Attempts to register the singleton instance for this class with ezApi. Returns true
        if successful, false otherwise.
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzVersion.#ezCanRegister && !EzVersion.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(EzVersion, EzVersion.ezApiName);
        }

        return EzVersion.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzVersion.ezApiName
     *     2) Property getter EzVersion.ezEventNames
     *     3) Property getter EzVersion.ezInstance
     *     4) Property setter EzVersion.ezInstance
     *     5) Property getter EzVersion.ezApiRegistrationState
     *     6) Property setter EzVersion.ezApiRegistrationState
     *     7) Property getter EzVersion.#ezCanRegister
     *     8) Property getter EzVersion.#ezIsRegistered
     *     9) Method EzVersion.#ezRegistrator()
     */
    static {
        if (!EzVersion.#ezIsRegistered) {
            EzVersion.ezApiRegistrationState = EzRegistrationState.PENDING;

            EzVersion.ezWaitDependenciesReady(
                EzVersion,
                [
                    EzEventEngine,
                    EzHttpHelper,
                    EzNavigation
                ],
                EzVersion.#ezRegistrator);
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
